import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useInvestDataState } from './_investDataState'
import { getAmount, setAmount } from '../shared/_api'

import { AmountTypes } from '../shared/_types'

export const amountQueryKeyRoot = ['amount'] as const

export const getAmountQueryKey = (investmentId?: string) =>
  [...amountQueryKeyRoot, investmentId] as const

export const useAmountDataState = () => {
  const { data: investData } = useInvestDataState()

  return useQuery({
    queryKey: getAmountQueryKey(investData?.investment?.id),
    queryFn: async () => {
      const data = await getAmount(investData.investment?.id)
      return {
        ...data,
      }
    },
    enabled:
      !!investData.investment?.id &&
      !location.pathname.includes('start') &&
      !location.pathname.includes('account'),
    staleTime: Infinity,
  })
}

export const useAmountMutation = () => {
  const { data: investData } = useInvestDataState()
  const queryClient = useQueryClient()
  const amountQueryKey = getAmountQueryKey(investData?.investment?.id)

  return useMutation({
    mutationFn: (data) =>
      setAmount(investData?.investment.id, {
        ...data,
      }),
    onMutate: async (newAmount: AmountTypes) => {
      await queryClient.cancelQueries({
        queryKey: amountQueryKey,
      })
      const previousAmount =
        queryClient.getQueryData<AmountTypes>(amountQueryKey)
      if (previousAmount) {
        queryClient.setQueryData<AmountTypes>(amountQueryKey, {
          ...previousAmount,
          ...newAmount,
        })
      }
      return { previousAmount }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['invest'],
      })
      queryClient.invalidateQueries({
        queryKey: amountQueryKeyRoot,
      })
    },
    onError: (_err, _variables, context) => {
      if (context?.previousAmount) {
        queryClient.setQueryData<AmountTypes>(
          amountQueryKey,
          context.previousAmount
        )
      }
    },
  })
}
